import { type RouteObject } from 'react-router'

import { scoutApp } from '~/apps/scout'
import { type RegisteredApp } from '~/shared/appContract'

import { addToAppNavCache } from './appNav'

/**
 * Every app in the suite, in the order they are offered.
 *
 * This is the only place the shell learns that an app exists: the router is assembled from this list
 * rather than from named imports, so adding an app is an entry here plus its own directory. Each entry
 * is an app's own public entrypoint, so an app declares its own identity — id, display copy, routes,
 * and its header nav — and the shell only arranges what it is given (Atlas ADR-0017).
 *
 * Order is meaningful. The first *enabled* entry is where a customer-scoped URL that names no app
 * lands, which is a deliberate placeholder for the entitlement fallback chain.
 *
 * This is a trimmed copy of arc's `shell/app/registry.ts`, deliberately the same module in the same
 * place: it is the only shell module that names an app, so it holds boundary rule 2's mounting
 * exemption, and when Scout moves into the suite this file is deleted in favour of arc's rather than
 * reconciled with it. Scout registers the one app it serves where arc registers two.
 */
export const appRegistry: RegisteredApp[] = [scoutApp]

/**
 * The apps a user may be sent to, in registry order.
 *
 * Every consumer that *offers* an app filters through this rather than reading `appRegistry` directly,
 * so a disabled app cannot be offered by one surface having forgotten to check the flag. Mounting
 * deliberately does not filter: a disabled app's URLs still resolve, which is what makes a stub
 * reachable while it is built. Scout registers no disabled app today; the seam is arc's, kept so the
 * consumers already read through it.
 */
export const getEnabledApps = (): RegisteredApp[] =>
  appRegistry.filter((app) => !app.disabled)

/**
 * The registered app with the given id, or `undefined` for an id no app claims.
 *
 * Reads the whole registry rather than `getEnabledApps`, because this answers "which app is this
 * route" and not "which app may a user be sent to". A **disabled** app is mounted, so a URL naming one
 * resolves and the header must render *that* app's nav rather than the previous app's or none. The
 * caller decides what a missing app means — outside any app there is simply no entry to find.
 */
export const appById = (
  appId: string | undefined,
): RegisteredApp | undefined =>
  appId === undefined ? undefined : appRegistry.find((app) => app.id === appId)

/**
 * Route metadata carrying the id of the app a route belongs to.
 *
 * Attached to each app's mount point below and read back with `useMatches` in `useActiveApp`, which is
 * how the shell knows the active app without an `:app` param.
 */
export interface AppRouteHandle {
  appId: string
}

/**
 * The registry's apps as routes to nest under the customer-scoped layout: one route per app.
 *
 * Each app is mounted under its own *literal* segment rather than a shared `:app` param. A single
 * param would collapse every app's table into one namespace, where React Router matches a child on its
 * own path alone and never checks that the child belongs to the app the URL names: one app's pages
 * would then resolve under another app's segment. A literal segment per app makes a cross-app path fail
 * to match at all. See Atlas ADR-0014.
 *
 * Everything below that segment is the app's own, which is what the trailing splat says: the shell
 * matches which app a URL names and hands the rest to the app, whose own `<Routes>` resolves it or
 * redirects. The component behind the segment arrives through `lazy`, so an app's pages are a chunk
 * fetched on the first navigation into it. The router awaits that chunk before committing the
 * navigation, so no app renders half-mounted. See Atlas ADR-0019.
 *
 * The loader caches the app's nav alongside loading its routes, rather than handing `loadRoutes`
 * straight to the router, which is what lets the header render the nav without a Suspense boundary:
 * both come from one module per app, so the second call costs no request, and the router awaits this
 * before it commits the navigation that renders the header. See Atlas ADR-0021 for what suspending
 * there costs instead.
 *
 * `lazy` here is the *function* form, and must stay so. Its per-property object form is single-use:
 * resolving it sets the consumed key to `undefined`, and because a route's `lazy` object is shared by
 * reference with every router built from it, the second router would find a route with neither a
 * component nor a loader and report the URL as unmatched.
 */
export const mountedAppRoutes = (): RouteObject[] =>
  appRegistry.map((app) => ({
    path: `${app.id}/*`,
    handle: { appId: app.id } satisfies AppRouteHandle,
    lazy: async () => {
      const [routeModule] = await Promise.all([
        app.loadRoutes(),
        addToAppNavCache(app),
      ])

      return routeModule
    },
  }))
